import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import SvgLogo from '../imports/SvgLogo1';

interface WelcomeScreenProps {
  /** Data fetching is still in progress */
  loading: boolean;
  /** Hero reel (images or video) has rendered its first frame */
  heroReady?: boolean;
  /** Minimum time the welcome screen stays visible (ms) */
  minDisplayTime?: number;
}

export function WelcomeScreen({ loading, heroReady = false, minDisplayTime = 3500 }: WelcomeScreenProps) {
  const [visible, setVisible] = useState(true);
  const [minTimeElapsed, setMinTimeElapsed] = useState(false);
  const [dismissing, setDismissing] = useState(false);

  // Ensure minimum display time so the animation plays out
  useEffect(() => {
    const timer = setTimeout(() => setMinTimeElapsed(true), minDisplayTime);
    return () => clearTimeout(timer);
  }, [minDisplayTime]);

  // Dismiss once ALL three conditions are met:
  // 1. Data loading is done
  // 2. Hero reel has its first frame painted
  // 3. Minimum display time has elapsed (so the user sees the welcome animation)
  useEffect(() => {
    if (!loading && heroReady && minTimeElapsed && !dismissing) {
      setDismissing(true);
    }
  }, [loading, heroReady, minTimeElapsed, dismissing]);

  // Safety fallback: never block longer than 10 seconds even if hero never fires ready
  useEffect(() => {
    const maxWait = setTimeout(() => {
      if (!dismissing) setDismissing(true);
    }, 10000);
    return () => clearTimeout(maxWait);
  }, [dismissing]);

  // Once dismissing starts, wait for exit animation to finish, then remove
  useEffect(() => {
    if (dismissing) {
      const timer = setTimeout(() => setVisible(false), 1800);
      return () => clearTimeout(timer);
    }
  }, [dismissing]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="welcome"
          initial={{ opacity: 1 }}
          animate={dismissing ? { opacity: 0 } : { opacity: 1 }}
          transition={{ duration: 1.6, ease: [0.4, 0, 0.2, 1] }}
          className="fixed inset-0 z-[9999] bg-black flex items-center justify-center"
          style={{ pointerEvents: dismissing ? 'none' : 'auto' }}
        >
          <div className="flex flex-col items-center">
            {/* Logo — fade in and scale up */}
            <motion.div
              initial={{ opacity: 0, scale: 0.85 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.0, ease: [0.25, 0.46, 0.45, 0.94], delay: 0.2 }}
              className="w-[80px] h-[80px] lg:w-[100px] lg:h-[100px] mb-8"
              style={{ '--fill-0': 'white' } as React.CSSProperties}
            >
              <div style={{ transform: 'scaleX(0.75)' }}>
                <SvgLogo />
              </div>
            </motion.div>

            {/* Gold accent line */}
            <motion.div
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 1.2, ease: [0.25, 0.46, 0.45, 0.94], delay: 0.6 }}
              className="w-10 h-px bg-yellow-400/60 mb-6 origin-center"
            />

            {/* Welcome text */}
            <motion.div
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: 'easeOut', delay: 1.0 }}
              className="text-neutral-500 text-xs tracking-[0.35em] uppercase"
              style={{ fontFamily: 'var(--font-freight)' }}
            >
              Welcome
            </motion.div>

            {/* Subtle pulsing dot — loading indicator */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: [0, 0.4, 0] }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: 1.4,
              }}
              className="w-1 h-1 rounded-full bg-yellow-400/40 mt-8"
            />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}